import { collection, doc, getDoc, orderBy, where, type QueryConstraint } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { err, ok, type Result } from '@shared/result';
import type { IncapacityId, PermissionRequestId } from '@shared/brand';
import type {
  AuditScope,
  DecidePermissionInput,
  IAuditLogRepository,
  IIncapacityRepository,
  ILogger,
  IPermissionRequestRepository,
  IncapacityScope,
  Page,
  PageRequest,
  PermissionScope,
  RegisterIncapacityInput,
  RepositoryError,
  SubmitPermissionInput,
} from '@application/ports';
import type { AuditEntry } from '@domain/entities/AuditEntry';
import type { Incapacity } from '@domain/entities/Incapacity';
import type { PermissionRequest } from '@domain/entities/PermissionRequest';
import { COLLECTIONS, firebase } from '../firebase/client';
import { decodeAuditEntry, decodeIncapacity, decodePermissionRequest } from '../codecs/entities';
import {
  runCountQuery,
  runPagedQuery,
  translateCallableError,
  translateFirestoreError,
} from './support';

/**
 * Adapters for permissions, incapacities and the audit log.
 *
 * Same shape as the vacation adapter: reads are direct and scoped, writes are
 * callables. The audit log has no write method at all — entries are created only by
 * Cloud Functions, which is what makes the log trustworthy.
 */

const call = async <Req, Res>(
  name: string,
  payload: Req,
  logger: ILogger,
): Promise<Result<Res, RepositoryError>> => {
  try {
    const { functions } = firebase();
    const invoke = httpsCallable<Req, Res>(functions, name);
    const response = await invoke(payload);
    return ok(response.data);
  } catch (cause) {
    logger.error(`callable ${name} failed`, { cause: String(cause) });
    return err(translateCallableError(cause, name));
  }
};

const readDoc = async <T>(
  collectionName: string,
  id: string,
  decode: (docId: string, data: Record<string, unknown>) => T | null,
): Promise<Result<T, RepositoryError>> => {
  try {
    const snapshot = await getDoc(doc(firebase().db, collectionName, id));
    if (!snapshot.exists()) return err({ kind: 'not-found', what: `${collectionName}/${id}` });

    const decoded = decode(snapshot.id, snapshot.data());
    return decoded === null
      ? err({
          kind: 'malformed-document',
          path: `${collectionName}/${id}`,
          detail: 'required field missing or invalid',
        })
      : ok(decoded);
  } catch (cause) {
    return err(translateFirestoreError(cause, `${collectionName}/${id}`));
  }
};

// ---------------------------------------------------------------------------
// Permissions (home office)
// ---------------------------------------------------------------------------

const permissionConstraints = (scope: PermissionScope): QueryConstraint[] => {
  const constraints: QueryConstraint[] = [];

  /*
   * Same rule as vacations: the area has to travel with the sub area.
   *
   * These two collections are read under `isSelf(personId) || (isManager() &&
   * inScope(areaId))`, and neither branch can be proved from a `subAreaId`
   * filter alone — so a leader's board was refused before it ran. `personId`
   * proves itself through the first branch and needs nothing beside it.
   */
  if (scope.personId !== undefined) {
    constraints.push(where('personId', '==', scope.personId));
  } else {
    if (scope.areaId !== undefined) constraints.push(where('areaId', '==', scope.areaId));
    if (scope.subAreaId !== undefined) constraints.push(where('subAreaId', '==', scope.subAreaId));
  }

  if (scope.statuses !== undefined && scope.statuses.length > 0) {
    constraints.push(where('status', 'in', [...scope.statuses]));
  }

  if (scope.touching !== undefined) {
    // Firestore allows one range filter, so the lower bound is issued here and the
    // upper bound applied in memory — same trade-off as the vacation adapter.
    constraints.push(where('endDate', '>=', scope.touching.start.toISO()));
    constraints.push(orderBy('endDate'));
  } else {
    constraints.push(orderBy('startDate', 'desc'));
  }

  return constraints;
};

export class FirestorePermissionRequestRepository implements IPermissionRequestRepository {
  constructor(private readonly logger: ILogger) {}

  getById(id: PermissionRequestId): Promise<Result<PermissionRequest, RepositoryError>> {
    return readDoc(COLLECTIONS.permissionRequests, id, decodePermissionRequest);
  }

  async list(
    scope: PermissionScope,
    page: PageRequest,
  ): Promise<Result<Page<PermissionRequest>, RepositoryError>> {
    const result = await runPagedQuery(
      collection(firebase().db, COLLECTIONS.permissionRequests),
      permissionConstraints(scope),
      page,
      decodePermissionRequest,
      'permissionRequests',
      (failures) => this.logger.warn('undecodable permissionRequest documents', { failures }),
    );
    if (!result.ok) return result;

    const window = scope.touching;
    return ok({
      items:
        window === undefined
          ? result.value.items
          : result.value.items.filter((p) => p.range.overlaps(window)),
      nextCursor: result.value.nextCursor,
    });
  }

  count(scope: PermissionScope): Promise<Result<number, RepositoryError>> {
    return runCountQuery(
      collection(firebase().db, COLLECTIONS.permissionRequests),
      permissionConstraints(scope),
      'permissionRequests count',
    );
  }

  async submit(
    input: SubmitPermissionInput,
  ): Promise<Result<PermissionRequestId, RepositoryError>> {
    const payload = {
      kind: input.kind,
      startDate: input.range.start.toISO(),
      endDate: input.range.end.toISO(),
      hours: input.hours,
      reason: input.reason,
    };
    const result = await call<typeof payload, { id: string }>(
      'submitPermissionRequest',
      payload,
      this.logger,
    );
    return result.ok ? ok(result.value.id as PermissionRequestId) : err(result.error);
  }

  decide(input: DecidePermissionInput): Promise<Result<void, RepositoryError>> {
    const payload = {
      id: input.id,
      decision: input.decision,
      reason: input.reason,
      suggestedRanges: input.suggestedRanges.map((r) => r.toISO()),
    };
    return call<typeof payload, void>('decidePermissionRequest', payload, this.logger);
  }

  reopen(id: PermissionRequestId, reason: string): Promise<Result<void, RepositoryError>> {
    return call<{ id: string; reason: string }, void>(
      'reopenPermissionRequest',
      { id, reason },
      this.logger,
    );
  }

  cancel(id: PermissionRequestId, reason: string): Promise<Result<void, RepositoryError>> {
    return call<{ id: string; reason: string }, void>(
      'cancelPermissionRequest',
      { id, reason },
      this.logger,
    );
  }
}

// ---------------------------------------------------------------------------
// Incapacities
// ---------------------------------------------------------------------------

const incapacityConstraints = (scope: IncapacityScope): QueryConstraint[] => {
  const constraints: QueryConstraint[] = [];

  /*
   * Same rule as vacations: the area has to travel with the sub area.
   *
   * These two collections are read under `isSelf(personId) || (isManager() &&
   * inScope(areaId))`, and neither branch can be proved from a `subAreaId`
   * filter alone — so a leader's board was refused before it ran. `personId`
   * proves itself through the first branch and needs nothing beside it.
   */
  if (scope.personId !== undefined) {
    constraints.push(where('personId', '==', scope.personId));
  } else {
    if (scope.areaId !== undefined) constraints.push(where('areaId', '==', scope.areaId));
    if (scope.subAreaId !== undefined) constraints.push(where('subAreaId', '==', scope.subAreaId));
  }

  if (scope.statuses !== undefined && scope.statuses.length > 0) {
    constraints.push(where('status', 'in', [...scope.statuses]));
  }

  constraints.push(orderBy('startDate', 'desc'));
  return constraints;
};

export class FirestoreIncapacityRepository implements IIncapacityRepository {
  constructor(private readonly logger: ILogger) {}

  getById(id: IncapacityId): Promise<Result<Incapacity, RepositoryError>> {
    return readDoc(COLLECTIONS.incapacities, id, decodeIncapacity);
  }

  list(
    scope: IncapacityScope,
    page: PageRequest,
  ): Promise<Result<Page<Incapacity>, RepositoryError>> {
    return runPagedQuery(
      collection(firebase().db, COLLECTIONS.incapacities),
      incapacityConstraints(scope),
      page,
      decodeIncapacity,
      'incapacities',
      (failures) => this.logger.warn('undecodable incapacity documents', { failures }),
    );
  }

  count(scope: IncapacityScope): Promise<Result<number, RepositoryError>> {
    return runCountQuery(
      collection(firebase().db, COLLECTIONS.incapacities),
      incapacityConstraints(scope),
      'incapacities count',
    );
  }

  async register(input: RegisterIncapacityInput): Promise<Result<IncapacityId, RepositoryError>> {
    const payload = {
      kind: input.kind,
      startDate: input.range.start.toISO(),
      endDate: input.range.end.toISO(),
      imssFolio: input.imssFolio,
      umf: input.umf,
      coverage: input.coverage,
      notes: input.notes,
      certificatePath: input.certificatePath,
      replacementPersonId: input.replacementPersonId,
    };
    const result = await call<typeof payload, { id: string }>(
      'registerIncapacity',
      payload,
      this.logger,
    );
    return result.ok ? ok(result.value.id as IncapacityId) : err(result.error);
  }

  validate(id: IncapacityId): Promise<Result<void, RepositoryError>> {
    return call<{ id: string }, void>('validateIncapacity', { id }, this.logger);
  }

  observe(id: IncapacityId, observation: string): Promise<Result<void, RepositoryError>> {
    return call<{ id: string; observation: string }, void>(
      'observeIncapacity',
      { id, observation },
      this.logger,
    );
  }

  cancel(id: IncapacityId, reason: string): Promise<Result<void, RepositoryError>> {
    return call<{ id: string; reason: string }, void>(
      'cancelIncapacity',
      { id, reason },
      this.logger,
    );
  }
}

// ---------------------------------------------------------------------------
// Audit log
// ---------------------------------------------------------------------------

const auditConstraints = (scope: AuditScope): QueryConstraint[] => {
  const constraints: QueryConstraint[] = [where('areaId', '==', scope.areaId)];

  if (scope.kind !== undefined) constraints.push(where('kind', '==', scope.kind));
  if (scope.actorPersonId !== undefined) {
    constraints.push(where('actorPersonId', '==', scope.actorPersonId));
  }

  // `createdAt` is a timestamp and the filters are calendar dates, so the bounds are
  // widened to the whole day at each end.
  if (scope.from !== undefined) {
    constraints.push(where('createdAt', '>=', new Date(`${scope.from.toISO()}T00:00:00`)));
  }
  if (scope.to !== undefined) {
    constraints.push(where('createdAt', '<=', new Date(`${scope.to.toISO()}T23:59:59`)));
  }

  constraints.push(orderBy('createdAt', 'desc'));
  return constraints;
};

/**
 * Read-only by construction.
 *
 * There is no write method, and the security rules deny create, update and delete to
 * every client. Entries exist only because a Cloud Function wrote them with the Admin
 * SDK — which is the whole reason the log is worth keeping.
 */
export class FirestoreAuditLogRepository implements IAuditLogRepository {
  constructor(private readonly logger: ILogger) {}

  list(scope: AuditScope, page: PageRequest): Promise<Result<Page<AuditEntry>, RepositoryError>> {
    return runPagedQuery(
      collection(firebase().db, COLLECTIONS.auditLog),
      auditConstraints(scope),
      page,
      decodeAuditEntry,
      'auditLog',
      (failures) => this.logger.warn('undecodable auditLog documents', { failures }),
    );
  }

  count(scope: AuditScope): Promise<Result<number, RepositoryError>> {
    return runCountQuery(
      collection(firebase().db, COLLECTIONS.auditLog),
      auditConstraints(scope),
      'auditLog count',
    );
  }

  getById(id: string): Promise<Result<AuditEntry, RepositoryError>> {
    return readDoc(COLLECTIONS.auditLog, id, decodeAuditEntry);
  }
}
